import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  CommandBar,
  IButtonStyles,
  ICommandBarItemProps,
  ICommandBarStyles,
  Text,
  Stack,
  ITextStyles,
  mergeStyles,
  MessageBar,
  ProgressIndicator,
  IProgressIndicatorStyles,
  MessageBarButton,
  MessageBarType,
} from '@fluentui/react';
import { Hamburger, Tooltip } from '@fluentui/react-components';
import { readTenantBrand } from '@moko/ui';
import {
  Cube24Regular,
  Building24Regular,
  Warning24Regular,
  ClipboardTask24Regular,
  QrCode24Regular,
  CalendarLtr24Regular,
  ArrowImport24Regular,
  Shield24Regular,
  Settings24Regular,
  Apps24Color,
  BoxMultiple24Regular,
  Briefcase24Regular,
  CalendarArrowCounterclockwise24Regular,
  DataArea24Color,
  Flowchart24Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  TaskListSquarePerson24Regular,
  DocumentTable24Regular,
} from '@fluentui/react-icons';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { loginRequest } from '../authConfig';
import { INotifMessage } from '../services/assetServices';
import { signOut } from '../services/authHelper';
import { getFmsToken } from '../auth/fmsToken';
import { getBuildNumber } from '../services/settingsService';
import { getAssetCategories } from '../services/categoryService';
import { getZones } from '../services/zoneService';
import AppContext from './AppContext';
import { useBoolean } from '@fluentui/react-hooks';
import InfoDialog from '../common/InfoDialog';
import logo from '../assets/MoKoLogo.svg';
import ChatCommandDialog from './ChatCommandDialog';
import MiniChart from './charts/MiniChart';
import { Actions, canDo } from '../actions';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
  messageAtom,
  messageBarAtom,
} from '../atoms/messageBarAtoms';
import {
  isAuthenticatedAtom,
  profileDataAtom,
  currentTenantNameAtom,
} from '../atoms/authAtoms';
import MyPermissions from './MyPermissions';
import TenantSelector from './TenantSelector';
import { notifAtom, notificationsAtom } from '../atoms/notifAtoms';
import Notifications from './Notifications';
import GlobalSearchBar from './GlobalSearchBar';
import { assetCategoriesAtom } from '../atoms/assetCategoriesAtom';
import { zonesAtom, zonesLoadedAtom } from '../atoms/zonesAtom';
import { tenantInfoAtom } from '../atoms/tenantAtom';
import { useTranslation } from 'react-i18next';

import {
  appEventEmitter,
  AUTH_ERROR_EVENT,
  FETCH_ERROR_EVENT,
} from '../services/eventEmitter';
import { Features, FeatureFlag } from '../libs/featureAccess';

// Nav item wrapper styles
const navItemWrapperStyle = (isActive: boolean): React.CSSProperties => ({
  borderLeft: isActive ? '3px solid #0078d4' : '3px solid transparent',
  backgroundColor: isActive ? '#f0f0f0' : 'transparent',
});

const navTextStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
};

// Nav item type
interface NavItemConfig {
  key: string;
  icon: React.ReactNode;
  label: string;
  url?: string;
  permission?: number;
  featureFlag?: Features;
  children?: { key: string; label: string; url: string }[];
}

// Nav item component
interface NavItemProps {
  icon?: React.ReactNode;
  label: string;
  isActive: boolean;
  isDisabled?: boolean;
  navExpanded: boolean;
  onClick: () => void;
  isExpanded?: boolean;
  childItems?: { key: string; label: string; url: string }[];
  activeNav?: string;
  onChildClick?: (url: string) => void;
}

const NavItem: React.FC<NavItemProps> = ({
  icon,
  label,
  isActive,
  isDisabled = false,
  navExpanded,
  onClick,
  isExpanded = false,
  childItems,
  activeNav,
  onChildClick,
}) => {
  const hasChildren = childItems && childItems.length > 0;

  return (
    <>
      <Tooltip content={label} relationship="label" positioning="after">
        <div
          className="nav-item"
          style={{
            ...navItemWrapperStyle(isActive),
            display: 'flex',
            alignItems: 'center',
            padding: navExpanded ? '8px 12px' : '8px',
            justifyContent: navExpanded ? 'flex-start' : 'center',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
            gap: '8px',
          }}
          onClick={() => !isDisabled && onClick()}
        >
          {icon}
          {navExpanded && (
            <>
              <div style={navTextStyle}>{label}</div>
              {hasChildren &&
                (isExpanded ? (
                  <ChevronDown20Regular />
                ) : (
                  <ChevronRight20Regular />
                ))}
            </>
          )}
        </div>
      </Tooltip>
      {hasChildren && isExpanded && navExpanded && (
        <div style={{ paddingLeft: '24px' }}>
          {childItems.map((child) => (
            <div
              key={child.key}
              className="nav-item"
              style={{
                ...navItemWrapperStyle(activeNav === child.key),
                padding: '6px 12px',
                cursor: 'pointer',
              }}
              onClick={() => onChildClick?.(child.url)}
            >
              <div style={navTextStyle}>{child.label}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const Home = (props: any) => {
  const { t } = useTranslation();

  let navigate = useNavigate();
  const location = useLocation();
  const context = useContext(AppContext);
  const message = useAtomValue(messageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const clearMessage = useSetAtom(clearMessageAtom);
  const profileData = useAtomValue(profileDataAtom);
  const [isInProgress, setIsInProgress] = useAtom(isInProgressAtom);
  const [notifications, setNotifications] = useAtom(notificationsAtom);
  const [assetCategories, setAssetCategories] = useAtom(assetCategoriesAtom);
  const [zones, setZones] = useAtom(zonesAtom);
  const [zonesLoaded, setZonesLoaded] = useAtom(zonesLoadedAtom);
  const isAuthenticatedAtomValue = useAtomValue(isAuthenticatedAtom);

  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [navExpanded, setNavExpanded] = useState(true);

  // Derive active nav from URL
  const activeNav = useMemo(() => {
    const path = location.pathname.replace(/^\//, '');
    if (path.startsWith('importAssets')) return 'importAssets';
    if (path.startsWith('importIssueTypes')) return 'importIssueTypes';
    if (path.startsWith('importZones')) return 'importZones';
    if (path.startsWith('settings')) return 'settings';
    if (path.startsWith('metadata')) return 'metadata';
    return path || 'home';
  }, [location.pathname]);

  useEffect(() => {
    const id = new URLSearchParams(location.search).get('id');

    const routeTabMap: Record<string, string> = {
      '/editAsset': 'Asset',
      '/editOrder': 'Order',
      '/editWork': 'Work',
    };

    const baseTab = routeTabMap[location.pathname];
    if (!baseTab) {
      return;
    }

    const nextTab = id ? `${baseTab} #${id}` : baseTab;
    if (context.selectedTab !== nextTab) {
      context.setSelectedTab(nextTab);
    }
  }, [context, context.selectedTab, location.pathname, location.search]);

  const _items: ICommandBarItemProps[] = [];

  const [
    isAboutModalOpen,
    { setTrue: showAboutModal, setFalse: hideAboutModal },
  ] = useBoolean(false);
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [
    isNotificationsOpen,
    { setTrue: showNotifications, setFalse: hideNotifications },
  ] = useBoolean(false);

  const [expandedNav, setExpandedNav] = useState(() =>
    location.pathname.includes('import') ? 'import' : ''
  );

  // Nav items config
  const navItems: NavItemConfig[] = [
    {
      key: 'assets',
      icon: <Cube24Regular style={{ color: 'green' }} />,
      label: 'Assets',
      url: '/assets',
      permission: Actions.ManageAssets,
      featureFlag: Features.FEATURE_ASSETS,
    },
    {
      key: 'zones',
      icon: <Building24Regular style={{ color: 'goldenRod' }} />,
      label: 'Zones',
      url: '/zones',
      permission: Actions.ManageZones,
      featureFlag: Features.FEATURE_ZONES,
    },
    {
      key: 'categories',
      icon: <BoxMultiple24Regular style={{ color: 'darkMagenta' }} />,
      label: 'Categories',
      url: '/categories',
      permission: Actions.ManageAssetsCategories,
      featureFlag: Features.FEATURE_CATEGORIES,
    },
    {
      key: 'renters',
      icon: (
        <CalendarArrowCounterclockwise24Regular
          style={{ color: 'dodgerBlue' }}
        />
      ),
      label: 'Rent',
      url: '/renters',
      permission: Actions.ManageRenters,
      featureFlag: Features.FEATURE_RENT,
    },
    {
      key: 'contractors',
      icon: (
        <TaskListSquarePerson24Regular style={{ color: 'darkGoldenRod' }} />
      ),
      label: t('contractors'),
      url: '/contractors',
      permission: Actions.ManageContractors,
      featureFlag: Features.FEATURE_CONTRACTORS,
    },
    {
      key: 'issues',
      icon: <Warning24Regular style={{ color: 'salmon' }} />,
      label: 'Issues',
      url: '/issues',
      permission: Actions.ManageIssues,
      featureFlag: Features.FEATURE_ISSUES,
    },
    {
      key: 'works',
      icon: <Briefcase24Regular style={{ color: 'mediumBlue' }} />,
      label: t('works'),
      url: '/works',
      permission: Actions.ManageWorks,
      featureFlag: Features.FEATURE_WORK_ORDERS,
    },
    {
      key: 'orders',
      icon: <ClipboardTask24Regular style={{ color: 'blueViolet' }} />,
      label: t('orders'),
      url: '/orders',
      permission: Actions.ManageOrders,
      featureFlag: Features.FEATURE_ORDERS,
    },
    {
      key: 'qrCodes',
      icon: <QrCode24Regular style={{ color: 'black' }} />,
      label: 'QR Codes',
      url: '/qrCodes',
      featureFlag: Features.FEATURE_QR_CODES,
    },
    {
      key: 'calendar',
      icon: <CalendarLtr24Regular style={{ color: 'green' }} />,
      label: 'Calendar',
      url: '/calendar',
      featureFlag: Features.FEATURE_CALENDAR,
    },
    {
      key: 'workflows',
      icon: <Flowchart24Regular style={{ color: 'dodgerBlue' }} />,
      label: 'Workflows',
      url: '/workflows',
      permission: Actions.ManageSettings,
      featureFlag: Features.FEATURE_WORKFLOWS,
    },
    {
      key: 'reports',
      icon: <DataArea24Color style={{ color: 'seaGreen' }} />,
      label: 'Analytics',
      url: '/reports',
      permission: Actions.Analytics,
      featureFlag: Features.FEATURE_REPORTS,
    },
    {
      key: 'reportTemplates',
      icon: <DocumentTable24Regular style={{ color: 'teal' }} />,
      label: 'Report template',
      url: '/reportTemplates',
      permission: Actions.Reports,
    },
    {
      key: 'import',
      icon: <ArrowImport24Regular />,
      label: 'Import',
      permission: Actions.Import,
      children: [
        { key: 'importAssets', label: 'Assets', url: '/importAssets' },
        {
          key: 'importIssueTypes',
          label: 'Issue types',
          url: '/importIssueTypes',
        },
        { key: 'importZones', label: 'Zones', url: '/importZones' },
      ],
    },
    {
      key: 'security',
      icon: <Shield24Regular style={{ color: 'tomato' }} />,
      label: 'Security',
      url: '/security',
      permission: Actions.Permissions,
    },
    {
      key: 'metadata',
      icon: <Apps24Color style={{ color: 'DarkSlateGray' }} />,
      label: 'Forms & Metadata',
      url: '/metadata',
      permission: Actions.ManageMetadata,
    },
    {
      key: 'settings',
      icon: <Settings24Regular />,
      label: 'Settings',
      url: '/settings/references',
      permission: Actions.ManageSettings,
    },
  ];

  const [buildNumber, setBuildNumber] = useState('');

  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  };

  const handleAboutClick = () => {
    const abortController = new AbortController();
    getBuildNumber(abortController)
      .then((buildNumber: string) => {
        setBuildNumber(buildNumber);
        showAboutModal();
      })
      .catch((reason: any) => {
        console.log(reason);
        setBuildNumber('Failed to fetch API Build number');
        showAboutModal();
      });
  };

  const currTenantInfo = useAtomValue(tenantInfoAtom);
  const tenantBrand = useMemo(
    () => readTenantBrand(currTenantInfo),
    [currTenantInfo]
  );

  const [isConnectedToHub, setIsConnectedToHub] = useState(false);
  const currentTenantName = useAtomValue(currentTenantNameAtom);

  const [
    isMyPermissionsOpen,
    { setTrue: showMyPermissions, setFalse: hideMyPermissions },
  ] = useBoolean(false);
  const [
    isTenantSelectorOpen,
    { setTrue: showTenantSelector, setFalse: hideTenantSelector },
  ] = useBoolean(false);

  const headerFarItemButtonStyles: Partial<IButtonStyles> = {
    root: {
      background: 'transparent',
      border: 'none',
    },
    rootHovered: {
      background: 'rgba(255, 255, 255, 0.18)',
    },
    rootPressed: {
      background: 'rgba(255, 255, 255, 0.24)',
    },
    rootExpanded: {
      background: 'rgba(255, 255, 255, 0.24)',
    },
    rootChecked: {
      background: 'rgba(255, 255, 255, 0.24)',
    },
    textContainer: {
      background: 'transparent',
    },
    label: {
      background: 'transparent',
    },
    icon: {
      background: 'transparent',
    },
    menuIcon: {
      background: 'transparent',
    },
  };

  const tenantButtonStyles: Partial<IButtonStyles> = {
    root: {
      background: 'rgba(0, 0, 0, 0.07)',
      border: 'none',
      borderRadius: '12px',
      padding: '0 10px',
      height: '28px',
      margin: 'auto 4px',
    },
    rootHovered: {
      background: 'rgba(0, 0, 0, 0.13)',
      borderRadius: '12px',
    },
    rootPressed: {
      background: 'rgba(0, 0, 0, 0.19)',
      borderRadius: '12px',
    },
    rootExpanded: {
      background: 'rgba(0, 0, 0, 0.19)',
      borderRadius: '12px',
    },
    textContainer: { background: 'transparent' },
    label: { background: 'transparent', fontSize: '13px' },
    icon: { background: 'transparent' },
    menuIcon: { display: 'none' },
  };

  const _farItems: ICommandBarItemProps[] = [
    {
      key: 'tenant',
      text: currentTenantName || undefined,
      ariaLabel: 'Select tenant',
      buttonStyles: tenantButtonStyles,
      onClick: () => showTenantSelector(),
    },
    {
      key: 'chatCommand',
      text: 'Chat to Command',
      ariaLabel: 'Chat to Command',
      iconOnly: true,
      iconProps: { iconName: 'ChatBot' },
      buttonStyles: headerFarItemButtonStyles,
      onClick: (ev) => toggleHideDialog(),
    },
    {
      key: 'connectionStatus',
      text: 'Connection Status',
      ariaLabel: 'Connection Status',
      iconOnly: true,
      iconProps: {
        iconName: isConnectedToHub ? 'PlugConnected' : 'PlugDisconnected',
      },
      buttonStyles: headerFarItemButtonStyles,
    },
    {
      key: 'notifications',
      text:
        notifications.length > 0 ? notifications.length.toString() : undefined,
      ariaLabel: 'Notifications',
      buttonStyles: {
        ...headerFarItemButtonStyles,
        textContainer: {
          background: 'transparent',
          position: 'absolute',
          left: 3,
          top: 5,
          backgroundColor: 'red',
          color: 'white',
          borderRadius: 5,
        },
      },
      iconProps: { iconName: 'Ringer' },
      onClick: () => showNotifications(),
    },
    !(isAuthenticated || isAuthenticatedAtomValue)
      ? {
          key: 'login',
          text: 'Login',
          ariaLabel: 'Login',
          iconOnly: true,
          iconProps: { iconName: 'FollowUser' },
          buttonStyles: headerFarItemButtonStyles,
          onClick: handleSignIn,
        }
      : {
          key: 'signedIn',
          text: `${profileData.givenName ?? ''} ${profileData.surname ?? ''}`,
          // This needs an ariaLabel since it's icon-only
          ariaLabel: 'Signed In',
          iconOnly: true,
          iconProps: { iconName: 'Contact' },
          buttonStyles: headerFarItemButtonStyles,
          subMenuProps: {
            items: [
              {
                key: 'profile',
                text: 'Profile',
                ariaLabel: 'Profile',
                onClick: async () => {
                  context.setIsProfileDialogOpen(true);
                },
              },
              {
                key: 'permissions',
                text: 'My permissions',
                ariaLabel: 'Permissions',
                onClick: async () => {
                  showMyPermissions();
                },
              },
              {
                key: 'logout',
                text: 'Logout',
                onClick: () => {
                  signOut(instance);
                },
              },
              {
                key: 'about',
                text: 'About',
                ariaLabel: 'About',
                onClick: handleAboutClick,
              },
            ],
          },
        },
  ];

  const handleNavClick = (url: string) => {
    clearMessage();
    context.setSelectedItem({ id: 0, uuid: '' });
    if (url) {
      navigate(url);
    }
  };

  const titleTextStyles: Partial<ITextStyles> = {
    root: {
      paddingLeft: '5px',
      paddingTop: '5px',
    },
  };

  const tenantTitleTextStyles: Partial<ITextStyles> = {
    root: {
      fontSize: '14px',
      fontWeight: 'bold',
    },
  };

  const subtitleTextStyles: Partial<ITextStyles> = {
    root: {
      paddingLeft: '12px',
      '@media (max-width: 599px)': {
        display: 'none',
      },
    },
  };
  const logoBackground = mergeStyles({});

  const progressBarStyles: IProgressIndicatorStyles = {
    itemDescription: '',
    itemName: '',
    itemProgress: { padding: '0px 0px' },
    progressBar: '',
    progressTrack: '',
    root: {},
  };

  const headerCommandBarStyles: Partial<ICommandBarStyles> = {
    root: {
      background: 'transparent',
      paddingLeft: 0,
    },
    primarySet: {
      background: 'transparent',
    },
    secondarySet: {
      background: 'transparent',
    },
  };

  const headerBackground =
    'linear-gradient(90deg, #b9e4f2 0%, #c6ebf6 42%, #d6f1fa 72%, #e6f7fd 100%)';

  const [connection, setConnection] = useState<null | HubConnection>(null);
  useEffect(() => {
    // Skip the notifications hub when no URL is configured (e.g. local dev
    // without VITE_NOTIF_API_URL). An empty URL makes SignalR negotiate against
    // the app origin, which 404s and then retries forever via automatic reconnect.
    const notifUrl = import.meta.env.VITE_NOTIF_API_URL;
    if (!notifUrl) return;

    const connect = new HubConnectionBuilder()
      .withUrl(notifUrl, {
        accessTokenFactory: () => getFmsToken(),
      })
      .withAutomaticReconnect()
      .build();

    setConnection(connect);
  }, []);

  const setNotif = useSetAtom(notifAtom);

  useEffect(() => {
    if (connection && connection.state === HubConnectionState.Disconnected) {
      connection
        .start()
        .then(() => {
          connection.on('newMessage', (message) => {
            console.log(message);
            setNotif(JSON.parse(message) as INotifMessage);
          });
        })
        .then(async () => {
          if (connection) {
            setIsConnectedToHub(true);
            await connection.send('sendMessage', 'echo');
          }
        })
        .catch((error) => {
          setIsConnectedToHub(false);
          console.log(error);
        });
    }
  }, [connection, context]);

  const notifValue = useAtomValue(notifAtom);
  const setMessageBar = useSetAtom(messageBarAtom);

  useEffect(() => {
    let message = '';
    if (notifValue.t === 'WOU') {
      const woId = Number(notifValue.b);
      message = `Work ${woId} has been updated`;
    } else if (notifValue.t === 'AU' || notifValue.t === 'ASU') {
      const assetId = Number(notifValue.b);
      message =
        notifValue.t === 'AU'
          ? `Asset ${assetId} has been updated`
          : `The status of asset ${assetId} has been changed`;
    } else if (notifValue.t === 'IC') {
      const issueId = Number(notifValue.b);
      message = `New issue has been reported: (Issue id: ${issueId})`;
    }
    if (message === '') {
      return;
    }
    setMessageBar(message, MessageBarType.warning);
    if (!notifications.find((f) => f.message === message)) {
      const newNotifications = [...notifications];
      newNotifications.push({ message });
      setNotifications(newNotifications);
    }
  }, [notifValue]);

  useEffect(() => {
    // Listener for authentication errors
    const handleAuthError = (message: string) => {
      setMessageBar(message, MessageBarType.error, [
        {
          actionLabel: 'Login',
          action: () => {
            console.log('Redirecting to login page...');
            handleSignIn();
          },
        },
      ]);
      console.log('Auth Error Received:', message);
    };

    // Listener for general fetch errors (non-auth related)
    const handleFetchError = (message: string) => {
      console.log('Fetch Error Received:', message);
      if (message.startsWith('API Error: 401')) {
        setMessageBar(message, MessageBarType.error, [
          {
            actionLabel: 'Login',
            action: () => {
              console.log('Redirecting to login page...');
              handleSignIn();
            },
          },
        ]);
        setErrorMessage(message);
      }
    };

    appEventEmitter.on(AUTH_ERROR_EVENT, handleAuthError);
    appEventEmitter.on(FETCH_ERROR_EVENT, handleFetchError);

    // Clean up event listeners on unmount to prevent memory leaks
    return () => {
      appEventEmitter.off(AUTH_ERROR_EVENT, handleAuthError);
      appEventEmitter.off(FETCH_ERROR_EVENT, handleFetchError);
    };
  }, [setErrorMessage]); // Dependency array includes setErrorMessage

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      // const metadata = await getMetadata(abortController, 'Asset');
      // setMetadata(metadata);

      const zones = await getZones(abortController);

      setZones(zones);
      setZonesLoaded(true);

      const categories = await getAssetCategories(abortController);
      setAssetCategories(categories);

      // const assetStatuses: IAssetStatuses[] = await getAssetStatuses(abortController);
      // setAssetStatuses(assetStatuses);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  return isAuthenticatedAtomValue ? (
    <div
      className="App"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        horizontal
        styles={{
          root: {
            backgroundImage: headerBackground,
            backgroundColor: '#e6f7fd',
            flexShrink: 0,
          },
        }}
      >
        <div
          className={logoBackground}
          onClick={() => navigate('/')}
          style={{ display: 'flex', cursor: 'pointer' }}
        >
          {currTenantInfo?.logo ? (
            <img
              src={currTenantInfo?.logo}
              alt="logo"
              style={{ height: '35px' }}
            />
          ) : (
            <Text variant="xxLarge" styles={titleTextStyles}>
              FMS
            </Text>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {currTenantInfo?.title ? (
              <div style={{ paddingLeft: '12px' }}>
                {currTenantInfo?.titleStyle ? (
                  <div style={JSON.parse(currTenantInfo.titleStyle)}>
                    {currTenantInfo?.title}
                  </div>
                ) : (
                  <Text styles={tenantTitleTextStyles}>
                    {currTenantInfo?.title}
                  </Text>
                )}
              </div>
            ) : null}
            {tenantBrand.subtitle ? (
              <Text styles={subtitleTextStyles}>{tenantBrand.subtitle}</Text>
            ) : null}
          </div>
        </div>
        <Stack.Item
          grow
          styles={{
            root: { display: 'flex', alignItems: 'center', padding: '0 12px' },
          }}
        >
          <GlobalSearchBar />
        </Stack.Item>
        <CommandBar
          className="Header"
          items={_items}
          farItems={_farItems}
          styles={headerCommandBarStyles}
          ariaLabel="Items actions"
          primaryGroupAriaLabel="Items actions"
          farItemsGroupAriaLabel="More actions"
        />
      </Stack>
      <Stack
        style={{
          flex: '1 1 auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ProgressIndicator
          styles={progressBarStyles}
          percentComplete={!isInProgress ? 0 : undefined}
        />
        <Stack horizontal style={{ flex: '1 1 auto', overflow: 'hidden' }}>
          {/* Navigation sidebar with smooth width transition */}
          <div
            style={{
              width: navExpanded ? '180px' : '48px',
              minWidth: navExpanded ? '180px' : '48px',
              borderRight: '1px solid #eee',
              background: '#faf9f8',
              flexShrink: 0,
              overflow: 'auto',
              transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
            }}
          >
            {/* Hamburger toggle */}
            <div
              style={{
                padding: '8px',
                display: 'flex',
                justifyContent: navExpanded ? 'flex-start' : 'center',
                borderBottom: '1px solid #eee',
              }}
            >
              <Tooltip
                content={navExpanded ? 'Close Navigation' : 'Open Navigation'}
                relationship="label"
              >
                <Hamburger onClick={() => setNavExpanded(!navExpanded)} />
              </Tooltip>
            </div>

            {/* Nav items */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {navItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isActive = hasChildren
                  ? activeNav.startsWith(item.key)
                  : activeNav === item.key;
                const isDisabled =
                  item.permission && !canDo(profileData, item.permission);
                const isExpanded = expandedNav === item.key;

                const navItemElement = (
                  <NavItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    isActive={isActive}
                    isDisabled={!!isDisabled}
                    navExpanded={navExpanded}
                    onClick={() => {
                      if (hasChildren) {
                        if (!navExpanded) {
                          setNavExpanded(true);
                          setExpandedNav(item.key);
                        } else {
                          setExpandedNav(isExpanded ? '' : item.key);
                        }
                      } else if (item.url) {
                        handleNavClick(item.url);
                      }
                    }}
                    isExpanded={isExpanded}
                    childItems={item.children}
                    activeNav={activeNav}
                    onChildClick={handleNavClick}
                  />
                );

                return item.featureFlag ? (
                  <FeatureFlag key={item.key} featureKey={item.featureFlag}>
                    {navItemElement}
                  </FeatureFlag>
                ) : (
                  navItemElement
                );
              })}
            </div>
          </div>
          <Stack
            grow
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: '1 1 auto',
            }}
          >
            {message.message ? (
              <MessageBar
                messageBarType={message.messageType}
                isMultiline={false}
                dismissButtonAriaLabel="Close"
                onDismiss={() => clearMessage()}
                actions={
                  <div>
                    {message.actions?.map((a) => (
                      <MessageBarButton onClick={a.action}>
                        {a.actionLabel}
                      </MessageBarButton>
                    ))}
                  </div>
                }
              >
                {message.message}
              </MessageBar>
            ) : (
              <></>
            )}
            <Stack
              horizontal
              disableShrink
              tokens={{
                childrenGap: 5,
              }}
              styles={{
                root: { flexShrink: 0, marginTop: 20, marginBottom: 5 },
              }}
            >
              <Stack.Item align="stretch" grow>
                <Text
                  variant="xLargePlus"
                  styles={{ root: { padding: '0px 14px 0px 25px' } }}
                >
                  {context.selectedTab}
                </Text>
              </Stack.Item>
              <Stack.Item align="end">
                <MiniChart></MiniChart>
              </Stack.Item>
            </Stack>
            {/* <Separator /> */}
            <div
              style={{
                padding: '10px 14px 25px 25px',
                overflow: 'auto',
                flex: '1 1 auto',
              }}
            >
              {props.children}
            </div>
          </Stack>
        </Stack>
      </Stack>
      <InfoDialog
        isModalOpen={isAboutModalOpen}
        hideModal={hideAboutModal}
        message={
          <>
            <p>
              <img
                src={logo}
                alt="MoKo Solution logo"
                style={{
                  width: 50,
                }}
              />
            </p>
            <p>{`Portal API build number: ${buildNumber}`}</p>
            <p>{`Portal UI build number:${
              import.meta.env.VITE_BUILD_NUMBER
            }`}</p>
          </>
        }
      />
      <ChatCommandDialog
        hideDialog={hideDialog}
        toggleHideDialog={toggleHideDialog}
      />
      <MyPermissions
        isOpen={isMyPermissionsOpen}
        hideMyPermissions={hideMyPermissions}
      />
      <TenantSelector
        isOpen={isTenantSelectorOpen}
        hideTenantSelector={hideTenantSelector}
      />
      <Notifications
        isOpen={isNotificationsOpen}
        hideNotifications={hideNotifications}
      />
    </div>
  ) : (
    <>
      {message.message ? (
        <MessageBar
          messageBarType={message.messageType}
          isMultiline={false}
          dismissButtonAriaLabel="Close"
          onDismiss={() => clearMessage()}
          actions={
            <div>
              {context.actions.map((a) => (
                <MessageBarButton onClick={a.action}>
                  {a.actionLabel}
                </MessageBarButton>
              ))}
            </div>
          }
        >
          {message.message}
        </MessageBar>
      ) : (
        <></>
      )}
    </>
  );
};

export default Home;
